export type AudioChunkHandler = (base64Pcm16: string) => void;

export const DEFAULT_INPUT_SAMPLE_RATE = 24000;
const MICROPHONE_CONSTRAINTS: MediaStreamConstraints = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true
  }
};

export class PcmRecorder {
  private context?: AudioContext;
  private source?: MediaStreamAudioSourceNode;
  private processor?: ScriptProcessorNode;
  private stream?: MediaStream;
  private onChunk?: AudioChunkHandler;

  async requestPermission() {
    const stream = await navigator.mediaDevices.getUserMedia(MICROPHONE_CONSTRAINTS);
    stream.getTracks().forEach((track) => track.stop());
  }

  async start(onChunk: AudioChunkHandler, targetSampleRate = DEFAULT_INPUT_SAMPLE_RATE) {
    if (this.context) return;
    this.onChunk = onChunk;
    this.stream = await navigator.mediaDevices.getUserMedia(MICROPHONE_CONSTRAINTS);

    this.context = new AudioContext();
    await this.resumeContext();
    this.source = this.context.createMediaStreamSource(this.stream);
    this.processor = this.context.createScriptProcessor(4096, 1, 1);

    this.processor.onaudioprocess = (event) => {
      const input = event.inputBuffer.getChannelData(0);
      const pcm = floatTo16BitPcm(downsample(input, this.context!.sampleRate, targetSampleRate));
      this.onChunk?.(arrayBufferToBase64(pcm.buffer));
    };

    this.source.connect(this.processor);
    this.processor.connect(this.context.destination);
    await this.resumeContext();
  }

  stop() {
    this.processor?.disconnect();
    this.source?.disconnect();
    this.stream?.getTracks().forEach((track) => track.stop());
    void this.context?.close();
    this.context = undefined;
    this.source = undefined;
    this.processor = undefined;
    this.stream = undefined;
    this.onChunk = undefined;
  }

  private async resumeContext() {
    if (this.context?.state === "suspended") {
      await this.context.resume();
    }
  }
}

export class PcmPlayer {
  private context: AudioContext;
  private nextStart = 0;
  private readonly sources = new Set<AudioBufferSourceNode>();

  constructor(private sampleRate = DEFAULT_INPUT_SAMPLE_RATE) {
    this.context = new AudioContext();
    this.nextStart = this.context.currentTime;
  }

  async resume() {
    if (this.context.state === "suspended") {
      await this.context.resume();
    }
  }

  play(base64Pcm16: string) {
    const bytes = base64ToUint8Array(base64Pcm16);
    const int16 = new Int16Array(bytes.buffer, bytes.byteOffset, Math.floor(bytes.byteLength / 2));
    const buffer = this.context.createBuffer(1, int16.length, this.sampleRate);
    const channel = buffer.getChannelData(0);

    for (let index = 0; index < int16.length; index += 1) {
      channel[index] = Math.max(-1, Math.min(1, int16[index] / 32768));
    }

    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.context.destination);
    this.sources.add(source);
    source.onended = () => {
      this.sources.delete(source);
      source.disconnect();
    };

    const startAt = Math.max(this.context.currentTime + 0.02, this.nextStart);
    source.start(startAt);
    this.nextStart = startAt + buffer.duration;
  }

  reset() {
    for (const source of this.sources) {
      try {
        source.stop();
      } catch {
        // The source may already have ended.
      }
      source.disconnect();
    }
    this.sources.clear();
    this.nextStart = this.context.currentTime;
  }
}

function downsample(input: Float32Array, inputRate: number, outputRate: number) {
  if (inputRate === outputRate) return input;
  const ratio = inputRate / outputRate;
  const outputLength = Math.floor(input.length / ratio);
  const output = new Float32Array(outputLength);

  for (let outputIndex = 0; outputIndex < outputLength; outputIndex += 1) {
    const start = Math.floor(outputIndex * ratio);
    const end = Math.min(input.length, Math.floor((outputIndex + 1) * ratio));
    let sum = 0;
    for (let inputIndex = start; inputIndex < end; inputIndex += 1) {
      sum += input[inputIndex];
    }
    output[outputIndex] = sum / Math.max(1, end - start);
  }

  return output;
}

function floatTo16BitPcm(input: Float32Array) {
  const output = new Int16Array(input.length);
  for (let index = 0; index < input.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, input[index]));
    output[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return output;
}

function arrayBufferToBase64(buffer: ArrayBufferLike) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }
  return btoa(binary);
}

function base64ToUint8Array(base64: string) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}
